import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { ActionFunctionArgs, HeadersFunction, LoaderFunctionArgs, json } from '@remix-run/node';
import { useActionData, useLoaderData, useNavigation } from '@remix-run/react';
import { KlaviyoAccountEvent, KlaviyoEvent, pushKlaviyoEvent } from '@theprintbar/bullmq';
import { GQLTokenTypeType, GetContentPageDocument, GetUserWithProfileDocument } from '@theprintbar/graphql';
import { authenticator } from '@theprintbar/keystone';
import { runSudoGraphql } from '@theprintbar/keystone/context';
import { cachedJsonDay, serverEnv, withEnsuredForm } from '@theprintbar/server';
import { WebRoutes, ensuredSearchObject, getSearchObject, withWebDomain } from '@theprintbar/shared';
import { BrandStarIcon, ButtonSize, HoneypotForm, Input, OutlineLinkButton, PrimaryButton, SimpleCTA } from '@theprintbar/ui';
import { z } from 'zod';
import { createToken } from '~/server/user.server';
import { contentMeta } from '~/utils/contentMeta';
import { AuthContainer } from '../components/AuthContainer';
import { forgotPasswordValidator } from './ForgotPasswordSchema';

export const meta = contentMeta;

export const headers: HeadersFunction = ({ loaderHeaders }) => ({
	'Cache-Control': loaderHeaders.get('Cache-Control') || '',
});

export const action = async ({ request }: ActionFunctionArgs) => {
	return withEnsuredForm(
		{
			validator: forgotPasswordValidator,
			request,
		},
		async ({ email }) => {
			const lowercaseEmail = email.toLowerCase();
			const { user } = await runSudoGraphql(GetUserWithProfileDocument, { where: { email: lowercaseEmail } });
			if (!!user) {
				const token = await createToken(user.id, GQLTokenTypeType.Reset);
				if (!token) throw 'Something went wrong';

				const link = `${withWebDomain(WebRoutes.ResetPassword, serverEnv)}?token=${token?.id}`;

				await pushKlaviyoEvent.addJob({
					event: KlaviyoEvent.Account,
					data: {
						event: KlaviyoAccountEvent.AccountForgotPassword,
						email: user.email,
						link,
					},
				});
			}

			return json({ success: true, error: '' });
		},
	);
};

const searchParamSchema = z.object({
	migrated: z.string().optional(),
});

export const loader = async ({ request }: LoaderFunctionArgs) => {
	await authenticator.isAuthenticated(request, {
		successRedirect: WebRoutes.Account.Landing,
	});

	const { migrated } = ensuredSearchObject(request, searchParamSchema);

	const { contentPage } = await runSudoGraphql(GetContentPageDocument, { where: { slug: 'forgot-password' } });

	if (!contentPage) throw new Error('Content page not found');

	return cachedJsonDay({
		contentPage,
		migrated,
	});
};

export default () => {
	const pendingSubmit = useNavigation().state !== 'idle';
	//@ts-ignore
	const { error, success } = useActionData<typeof action>() || {};
	const { contentPage, migrated } = useLoaderData<typeof loader>();

	if (migrated && !!contentPage.heroBanner.structure?.[0]) {
		contentPage.heroBanner.structure[0].title = 'Welcome back!';
	}

	return (
		<AuthContainer
			content={contentPage}
			title={migrated ? 'Welcome Back' : undefined}
			subtitle={migrated ? "We've upgraded to a new site, and for security we need you to set a new password" : undefined}
		>
			{success ? (
				<div className="space-y-6">
					<p>If an account with this email exists, you should receive an email shortly.</p>
					<OutlineLinkButton icon={ArrowUpRightIcon} to={WebRoutes.Home}>
						Return home
					</OutlineLinkButton>
				</div>
			) : (
				<HoneypotForm
					honeyField="name"
					validator={forgotPasswordValidator}
					defaultValues={{
						email: migrated,
					}}
				>
					<div>
						<Input
							label="Email"
							placeholder="Email"
							name="email"
							type="email"
							disabled={pendingSubmit}
							autoComplete="email"
							inputClassName="bg-transparent !border-blue"
						/>
						<PrimaryButton
							type="submit"
							disabled={pendingSubmit}
							spinner={pendingSubmit}
							className="w-full text-xl font-display font-light"
							icon={BrandStarIcon}
							iconClass="fill-white stroke-white"
							iconSize={ButtonSize.M}
						>
							Send reset email
						</PrimaryButton>
					</div>
					{error && <div className="text-red-500">{error}</div>}
				</HoneypotForm>
			)}
			<SimpleCTA content={contentPage} containerClassName="mt-8" />
		</AuthContainer>
	);
};
